'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'

import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Skeleton,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TeamItem {
  id: string | null
  name: string
  image: string | null
}

const ALL_ITEM: TeamItem = { id: null, name: 'Semua', image: null }
const NO_CLASS_ITEM: TeamItem = { id: NO_SCHOOL_CLASS_ID, name: 'Tanpa kelas', image: null }

/** A real class has an avatar; the "Semua" and "Tanpa kelas" sentinels do not. */
const isRealClass = (id: string | null) => id !== null && id !== NO_SCHOOL_CLASS_ID

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function TeamAvatar({ name, image }: { name: string; image: string | null }) {
  return (
    <Avatar size='sm' profile='organization' className='srd:shrink-0'>
      <AvatarImage src={image ?? undefined} alt={name} />
      <AvatarFallback>{getInitials(name)}</AvatarFallback>
    </Avatar>
  )
}

const TRIGGER_CLASSES =
  'srd:border-outline srd:text-neutral-black srd:focus-visible:border-blue-100 srd:focus-visible:ring-blue-20 srd:flex srd:w-full srd:min-w-0 srd:items-center srd:justify-between srd:gap-1.5 srd:rounded-lg srd:data-[size=sm]:rounded-[min(var(--radius-md),10px)] srd:border srd:bg-neutral-white srd:py-2 srd:pr-2 srd:pl-2.5 srd:whitespace-nowrap srd:transition-colors srd:outline-none srd:select-none srd:focus-visible:ring-[3px] srd:disabled:cursor-not-allowed srd:disabled:bg-grey-10 srd:disabled:opacity-50 srd:data-[size=default]:h-12 srd:data-[size=sm]:h-8'

// ---------------------------------------------------------------------------
// Presentational base — no data fetching, safe to use in Storybook
// ---------------------------------------------------------------------------

export interface SchoolClassSelectBaseProps {
  value: string | null
  onValueChange: (value: string | null) => void
  teams: { id: string; name: string; image: string | null }[]
  isLoading?: boolean
  size?: 'sm' | 'default'
  className?: string
  disabled?: boolean
  showDash?: boolean
  /** Show a distinct "Tanpa kelas" option whose value is `NO_SCHOOL_CLASS_ID` (children with no class). */
  showNoClass?: boolean
  /** Show the "Semua" (all classes) option. Disable for members so they can only pick their own classes. */
  allowAll?: boolean
}

export function SchoolClassSelectBase({
  value,
  onValueChange,
  teams,
  isLoading = false,
  size = 'default',
  className,
  disabled,
  showDash = false,
  showNoClass = false,
  allowAll = true,
}: SchoolClassSelectBaseProps) {
  const items = React.useMemo<TeamItem[]>(
    () => [
      ...(allowAll ? [ALL_ITEM] : []),
      ...(showNoClass ? [NO_CLASS_ITEM] : []),
      ...teams.map((t) => ({ id: t.id, name: t.name, image: t.image })),
    ],
    [teams, showNoClass, allowAll],
  )

  const selectedItem = React.useMemo(
    () => items.find((item) => item.id === value) ?? (allowAll ? ALL_ITEM : (items[0] ?? ALL_ITEM)),
    [items, value, allowAll],
  )

  // When "Semua" is not allowed (members), never leave the value on a stale/null selection —
  // coerce to the first available class.
  React.useEffect(() => {
    if (allowAll || items.length === 0) return
    const valueInItems = items.some((item) => item.id === value)
    if (!valueInItems && items[0]) onValueChange(items[0].id)
  }, [allowAll, items, value, onValueChange])

  if (isLoading) {
    return <Skeleton className={cn('srd:w-full srd:rounded-lg', size === 'sm' ? 'srd:h-8' : 'srd:h-12', className)} />
  }

  // Member with no assigned classes — show info popover instead of empty select.
  // When "Tanpa kelas" is enabled there is still a meaningful choice, so keep the select.
  if (teams.length === 0 && !showNoClass) {
    return (
      <Popover>
        <PopoverTrigger
          data-size={size}
          className={cn(TRIGGER_CLASSES, typographyVariants({ variant: 'body-lg' }), className)}
        >
          <span className='srd:flex-1 srd:text-left srd:truncate'>{showDash ? 'Tidak ada kelas' : 'Semua'}</span>
          <ChevronDownIcon className='srd:text-blue-100 srd:size-4 srd:pointer-events-none srd:shrink-0' />
        </PopoverTrigger>
        <PopoverContent align='start' sideOffset={6}>
          <PopoverHeader>
            <PopoverTitle className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-black')}>
              Belum ada kelas
            </PopoverTitle>
            <PopoverDescription className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
              {showDash
                ? 'Kamu belum ditugaskan mengampu di kelas mana pun di sekolah ini.'
                : 'Belum ada kelas yang dibuat di sekolah ini.'}
            </PopoverDescription>
          </PopoverHeader>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Combobox<TeamItem>
      value={selectedItem}
      onValueChange={(item) => onValueChange(item?.id ?? null)}
      items={items}
      isItemEqualToValue={(a, b) => a.id === b.id}
      disabled={disabled}
    >
      <ComboboxTrigger
        render={
          <button
            type='button'
            disabled={disabled}
            data-size={size}
            className={cn(TRIGGER_CLASSES, typographyVariants({ variant: 'body-lg' }), className)}
          >
            {isRealClass(selectedItem.id) && <TeamAvatar name={selectedItem.name} image={selectedItem.image} />}
            <span className='srd:flex-1 srd:text-left srd:truncate'>{selectedItem.name}</span>
            <ChevronDownIcon className='srd:text-blue-100 srd:size-4 srd:pointer-events-none srd:shrink-0' />
          </button>
        }
      />
      <ComboboxContent>
        <ComboboxInput placeholder='Cari kelas...' showTrigger={false} inputSize='sm' />
        <ComboboxEmpty>Kelas tidak ditemukan.</ComboboxEmpty>
        <ComboboxList>
          {(item: TeamItem) => (
            <ComboboxItem key={item.id ?? 'all'} value={item}>
              {isRealClass(item.id) && <TeamAvatar name={item.name} image={item.image} />}
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:flex-1')}>{item.name}</span>
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

// ---------------------------------------------------------------------------
// Connected component — fetches teams based on role
// ---------------------------------------------------------------------------

export interface SchoolClassSelectProps {
  value: string | null
  onValueChange: (value: string | null) => void
  size?: 'sm' | 'default'
  className?: string
  disabled?: boolean
  /** Show a "Tanpa kelas" option (value `NO_SCHOOL_CLASS_ID`). Only rendered for owner/admin. */
  showNoClass?: boolean
}

export function SchoolClassSelect({
  value,
  onValueChange,
  size,
  className,
  disabled,
  showNoClass = false,
}: SchoolClassSelectProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()

  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: isAdminOrOwner,
  })

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: !isAdminOrOwner && !isRolePending,
  })

  const isLoading = isRolePending || (isAdminOrOwner ? allTeamsQuery.isLoading : myTeamsQuery.isLoading)

  const rawTeams = isAdminOrOwner ? (allTeamsQuery.data ?? []) : (myTeamsQuery.data ?? [])
  const teams = rawTeams.map((t) => ({ id: t.id, name: t.name, image: t.image ?? null }))

  return (
    <SchoolClassSelectBase
      value={value}
      onValueChange={onValueChange}
      teams={teams}
      isLoading={isLoading}
      size={size}
      className={className}
      disabled={disabled}
      showDash={!isAdminOrOwner}
      showNoClass={showNoClass && isAdminOrOwner}
      allowAll={isAdminOrOwner}
    />
  )
}
